/**
 * Shared data utility functions used across multiple components.
 * Extracted from InspectorPanel, InspectorTabs, SchemaDraftWorkspace,
 * SchemaSetManager, and DataUploadDashboard to eliminate duplication.
 */

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const toRecord = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

export const prettyPrint = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value);
  }
};

export const normaliseIdentifier = (value?: string | null): string =>
  value ? value.trim().toLowerCase() : '';

export const parseTimestamp = (value?: string | null): number => {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatTimestamp = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

export const stringToArray = (value: string): string[] =>
  value
    .split(/[,;\n]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

export const arrayToInputString = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

export const normaliseArrayInput = (value: string): string[] | undefined => {
  const arr = stringToArray(value);
  return arr.length > 0 ? arr : undefined;
};

export const cleanObject = (input: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return;
      }
      result[key] = trimmed;
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    if (typeof value === 'object' && Object.keys(value as Record<string, unknown>).length === 0) {
      return;
    }
    result[key] = value;
  });
  return result;
};

export const clampConfidence = (value: number | null): number | null => {
  if (value === null || Number.isNaN(value)) {
    return null;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return parseFloat(value.toFixed(2));
};

export const humaniseKey = (key: string): string => {
  if (!key) {
    return key;
  }
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const formatEnrichmentValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '—';
    }
    return value
      .map((entry) => {
        if (entry === null || entry === undefined) {
          return '';
        }
        return typeof entry === 'string' ? entry : JSON.stringify(entry);
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      return '—';
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return '—';
  }
  return String(value);
};
